import * as React from "react";
import type { OnClickHandler } from "hooks/use-on-click-from-action";
import { BigLabel, BigLabelContainer } from "../../views/labels/labels";
import { getLocalizedString } from "common/components/localization";
import { styled, css } from "../../../styled-components";
import SeeAllButton from "../../views/see-all-button/see-all-button";
import { getScrollBarWidth } from "sharedUI/lib/scrollbar-width";
import type { ListGroup } from "../list/list-types";
import { assert } from "common/Support";
import type { AppKind } from "common/components/app-kind";

interface InnerProps {
    cols: number;
    colWidth: number;
    screenWidth: number;
    scrollBarWidth: number;
}

const HorizontalListStyle = styled.div<InnerProps>`
    display: flex;
    flex-direction: column;
    width: ${p => p.screenWidth}px;

    padding: 8px 0;

    .outer {
        position: relative;
        overflow: hidden;
        padding-bottom: 4px;
    }

    .scroller {
        overflow-x: scroll;
        -webkit-overflow-scrolling: touch;
        display: flex;

        ${p =>
            p.colWidth > 20 &&
            css`
                scroll-snap-type: x mandatory;
                scroll-padding-inline: 16px;
                scroll-padding-left: 16px;
            `}
        margin-bottom: -${p => p.scrollBarWidth}px;

        ::-webkit-scrollbar {
            display: none;
        }
    }

    .inner {
        display: grid;
        grid-template-columns: repeat(${p => p.cols}, ${p => p.colWidth}px);
        grid-column-gap: ${p => p.theme.pageMarginMedium / 2}px;
        padding: 0 ${p => p.theme.pageMarginMedium}px;
        height: 100%;

        & > * {
            scroll-snap-align: start;
        }
    }
`;

interface Props<T> {
    readonly title: string;
    readonly onSeeAllClick: OnClickHandler | undefined;
    readonly groups?: readonly ListGroup<T>[];
    readonly items?: readonly T[];
    readonly children: (item: T, index: number | string) => React.ReactNode;
    readonly colWidth: number;
    readonly screenWidth: number;
    readonly appKind: AppKind;
}

interface State {
    readonly isPressed: boolean;
}

export default class HorizontalList<T> extends React.PureComponent<Props<T>, State> {
    public state: State = {
        isPressed: false,
    };
    private startX = 0;
    private scrollLeft = 0;
    private moved = false;

    private scrollerRef = React.createRef<HTMLDivElement>();
    private scrollBarWidth = getScrollBarWidth(0);

    private onMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (this.scrollerRef.current === null) return;

        this.setState({
            isPressed: true,
        });
        this.startX = event.pageX - this.scrollerRef.current.offsetLeft;
        this.scrollLeft = this.scrollerRef.current.scrollLeft;
        this.moved = false;
    };

    private onMouseLeaveUp = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        this.setState({
            isPressed: false,
        });
    };

    private onMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!this.state.isPressed || this.scrollerRef.current === null) return;

        const x = event.pageX - this.scrollerRef.current.offsetLeft;
        const walk = x - this.startX;

        this.scrollerRef.current.scrollLeft = this.scrollLeft - walk;

        if (Math.abs(walk) > 20) {
            this.moved = true;
        }
    };

    private onClickCapture = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (this.moved) {
            event.stopPropagation();
            this.moved = false;
        }
    };

    private onSeeAllClickHandler = async () => {
        if (this.props.onSeeAllClick === undefined) {
            return;
        }
        await this.props.onSeeAllClick(false);
    };

    public render(): React.ReactNode {
        // eslint-disable-next-line prefer-const
        let { items, groups, colWidth, onSeeAllClick, screenWidth, title } = this.props;
        const { isPressed } = this.state;

        if (items !== undefined) {
            groups = [
                {
                    title,
                    items,
                    onSeeAll: onSeeAllClick !== undefined ? this.onSeeAllClickHandler : undefined,
                },
            ];
        }

        assert(groups !== undefined);

        let result: React.ReactNode = groups.map((g, index) => {
            const showTitleBar = g.title !== "" || onSeeAllClick !== undefined;

            return (
                <HorizontalListStyle
                    key={index}
                    data-test="app-horizontal-list"
                    screenWidth={screenWidth}
                    colWidth={colWidth}
                    cols={g.items.length}
                    scrollBarWidth={this.scrollBarWidth}>
                    {showTitleBar && (
                        <BigLabelContainer>
                            <BigLabel>{g.title}</BigLabel>
                            {g.onSeeAll !== undefined && (
                                <SeeAllButton onClick={g.onSeeAll}>
                                    {getLocalizedString("seeAll", this.props.appKind)}
                                </SeeAllButton>
                            )}
                        </BigLabelContainer>
                    )}
                    <div className="outer">
                        <div
                            ref={this.scrollerRef as any}
                            className="scroller"
                            onMouseMove={isPressed ? this.onMouseMove : undefined}
                            onMouseUp={isPressed ? this.onMouseLeaveUp : undefined}
                            onMouseDown={this.onMouseDown}
                            onMouseLeave={isPressed ? this.onMouseLeaveUp : undefined}
                            onClickCapture={this.onClickCapture}>
                            <div className="inner">{g.items.map((item, ind) => this.props.children(item, ind))}</div>
                        </div>
                    </div>
                </HorizontalListStyle>
            );
        });

        if (items === undefined && title !== undefined) {
            result = (
                <>
                    <BigLabelContainer>
                        <BigLabel>{title}</BigLabel>
                    </BigLabelContainer>
                    {result}
                </>
            );
        }

        return result;
    }
}
